import BasePage from "../../baseComponents/BasePage";
import {Box, Grid, Stack, Typography} from "@mui/material";
import {keyframes, useTheme} from "@mui/material/styles";

import React, {useEffect, useState} from 'react';
import { Fab, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {Link} from "react-router-dom";
import {LinksIconMenu} from "./LinksIconMenu";
import {toolTutorialLinks} from "../../../utils/globals";
import {CenterOutDivider} from "../../atomicComponents/CenterOutDivier";


const colorShadowIn = (toColor: string) => keyframes`
  from { color: rgba(255,255,255,0);
         text-shadow: 2px 2px 6px rgba(0,0,0,0), -2px -2px 6px rgba(128,128,128,0); }
  60%  { color: rgba(255,255,255,0.85); }
  to   { color: ${toColor};
         text-shadow: 2px 2px 6px rgba(0,0,0,.9), -2px -2px 6px rgba(128,128,128,.6); }
`;

const x = {label: '', link: ''}

const pyLinks = [
  {label: 'astropy', link: 'https://www.astropy.org/'},
  {label: 'sunpy', link: 'https://sunpy.org/'},
  {label: 'hvpy', link: 'https://hvpy.readthedocs.io/'},
  {label: 'psipy', link: 'https://psipy.readthedocs.io/'},
]

const apiLinks = [
  {label: 'Helioviewer', link: 'https://api.helioviewer.org/docs/v2/'},
  {label: 'JPL\'s Horizons', link: 'https://ssd.jpl.nasa.gov/horizons/'},
  {label: 'SPDF\'s COHOWeb', link: 'https://omniweb.gsfc.nasa.gov/coho/'},
]

const tutorialLinks = [
  {label: 'MASdb', link: toolTutorialLinks.masdb},
  {label: 'Plot1D', link: toolTutorialLinks.plot1d},
  {label: 'Plot2D', link: toolTutorialLinks.plot2d},
  {label: 'Data Products', link: toolTutorialLinks.data_products},
  {label: 'Time Series', link: toolTutorialLinks.time_series},
  {label: 'Spacecraft Mapping', link: toolTutorialLinks.spacecraft_mapping},
  {label: 'White Light', link: toolTutorialLinks.white_light},
  {label: 'Coronagraphs', link: toolTutorialLinks.coronagraphs},
  {label: 'EUV & X-Rays', link: toolTutorialLinks.euv},
  {label: 'EUV Imagers', link: toolTutorialLinks.euv_comparison},
  {label: 'Data Access', link: toolTutorialLinks.data_access},
  {label: 'Demonstration', link: toolTutorialLinks.demonstration},
]

const psiLinks = [
  {label: 'MHDweb [v1]', link: 'https://www.predsci.com/mhdweb/home.php'},
  {label: 'Publications', link: 'https://www.predsci.com/portal/publications.php'},
  {label: 'MAS Model', link: 'https://www.predsci.com/corona/model_desc.html'},
  {label: 'Coronal Modeling', link: 'https://www.predsci.com/corona/'},
  {label: 'CORHEL-CME', link: 'https://ccmc.gsfc.nasa.gov/models/CORHEL-CME~1/'},
  {label: 'CORHEL [v5]', link:'https://ccmc.gsfc.nasa.gov/models/CORHEL-MAS_WSA_ENLIL~5.0/'},
]

const missionLinks = [
  {label: 'HMI', link: 'http://hmi.stanford.edu/'},
  {label: 'Parker', link: 'https://parkersolarprobe.jhuapl.edu/'},
  {label: 'SolO', link: 'https://www.esa.int/Science_Exploration/Space_Science/Solar_Orbiter'},
  {label: 'STEREO', link: 'https://stereo.gsfc.nasa.gov/'},
  {label: 'SOHO', link: 'https://soho.nascom.nasa.gov/'},
  {label: 'MLSO', link: 'https://www2.hao.ucar.edu/mlso'},
  {label: 'NSO', link: 'https://nso.edu/'},
  {label: 'SDO', link: 'https://sdo.gsfc.nasa.gov/'},
]

const LinksMenu = ({ title, items }) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(Array(items.length).fill(false));

  useEffect(() => {
    items.forEach((_, i) => {
      setTimeout(() => {
        setVisible(prev => {
          const newVisible = [...prev];
          newVisible[i] = true;
          return newVisible;
        });
      }, i * 100); // adjust stagger timing
    });
  }, [items]);

  return (
    <Stack spacing={1}>
      <Typography
        sx={(theme) => ({
          textAlign: 'center',
          fontSize: 'large',
          py: 1,
          color: theme.palette.background.main,
          animation: `${colorShadowIn(theme.palette.common.white)} 900ms ease-out forwards`,
          animationDelay: '0.2s',
        })}

      >
        {title}
      </Typography>
      {items.map((item, index) => (
        <Button
          key={index}
          component={Link}
          to={item.link}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          sx={{
            height: '4em',
            textTransform: 'none',
            borderRadius: '4px',
            fontSize: 'x-small',
            padding: 0,
            opacity: visible[index] ? 1 : 0,
            textOverflow: 'ellipsis',
            transform: visible[index]
              ? 'translateY(0px)'
              : `translateY(-10px)`,
            transition: `transform 0.4s ease ${index * 0.05}s, opacity 0.4s ease ${index * 0.05}s`,
            color: 'white',
            '&:hover': {
              textShadow: '2px 2px 6px lightblue, -2px -2px 6px lightyellow',
              fontWeight: 'bolder'
            },
          }}
        >
          {item.label}
        </Button>
      ))}
    </Stack>
  );
};


export const References = () => {
  const theme = useTheme();
  return (
    <BasePage>
      <Box width={'100%'} display={'flex'} justifyContent={'flex-end'}>
        <LinksIconMenu sx={{width: 100, justifyContent: 'right'}}/>
      </Box>
      <CenterOutDivider sx={{my:0, mb:1}} thickness={3} color="grey" animate delay={0} />
      <Box width={'100%'}>
      <Grid
        container
        spacing={1}
        justifyContent="center"
      >
        <Grid item xs={12} sm={2.4}>
        <LinksMenu title={'APIs'} items={apiLinks}/>
        </Grid>
        <Grid item xs={12} sm={2.4}>
        <LinksMenu title={'Libraries'} items={pyLinks}/>
          </Grid>
          <Grid item xs={12} sm={2.4}>
        <LinksMenu title={'Tutorials'} items={tutorialLinks}/>
            </Grid>
        <Grid item xs={12} sm={2.4}>
        <LinksMenu title={'Missions'} items={missionLinks}/>
        </Grid>
        <Grid item xs={12} sm={2.4}>
        <LinksMenu title={'PSI'} items={psiLinks}/>
        </Grid>
      </Grid>
        </Box>
      {/*</Stack>*/}
    </BasePage>
  );
}